import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, useDataGridColumnsState } from '@ibsys/kit';
import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnVisibilityState,
  DataGridSidePanelId,
} from '@ibsys/kit';
import type {
  AllDetailsFiltersState,
  AllDetailsSortState,
  DashboardQualitySettings,
  QualityColumnDescriptor,
  QualityDetailItem,
} from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { DASHBOARD_BLOCK_MAX_HEIGHT, preventShiftSelection } from '@/shared/lib';
import { MANDATORY_COLUMN_IDS, RECORD_FIELD_COLUMN_IDS, useAllDetailsColumns } from './useAllDetailsColumns';
import { useAllDetailsSorting } from './useAllDetailsSorting';
import { useAllDetailsFilters } from './useAllDetailsFilters';

/** Состояние боковой панели таблицы: какая открыта, закреплена ли и ширина, выставленная вручную */
export type AllDetailsSidePanelState = {
  /** Открытая панель; `null` — все закрыты */
  activePanel: DataGridSidePanelId | null;
  /** Панель закреплена продолжением таблицы, а не выезжает поверх неё */
  pinned: boolean;
  /** `undefined` — ширину подбирает сама панель */
  columnsPanelWidth?: number;
};

type Props = {
  /** Число оценок всей выборки — им подписана таблица и по нему рассчитана прокрутка */
  totalCount: number;
  /** Размер окна выдачи */
  pageSize: number;
  /** Сигнатура отбора: её смена начинает набор заново */
  cacheVersion: string;
  /** Загрузка одного окна выдачи */
  fetchPage: (offset: number, limit: number, signal: AbortSignal) => Promise<QualityDetailItem[]>;
  /** Строка-образец набора: по ней имя критерия сопоставляется его идентификатором */
  sampleRow: QualityDetailItem | null;
  blockNames: string[];
  availableColumns: QualityColumnDescriptor[];
  availableAttributes: ConversationAttributeItem[];
  columnSettings: DashboardQualitySettings['allDetailsColumns'];
  onChangeColumnSettings: (next: { visibleColumns: string[]; columnOrder: string[] }) => void;
  sort: AllDetailsSortState | null;
  onSortChange: (next: AllDetailsSortState | null) => void;
  filters: AllDetailsFiltersState;
  onFiltersChange: (next: AllDetailsFiltersState) => void;
  /** Состояние боковой панели — хранится выше таблицы и переживает её пересоздание */
  sidePanel: AllDetailsSidePanelState;
  /** Изменение части состояния боковой панели */
  onSidePanelChange: (patch: Partial<AllDetailsSidePanelState>) => void;
  /** Открытие диалога строки; `openInNewTab` — пользователь кликнул с shift */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

// По умолчанию закрепляем только узкую колонку-ссылку: несколько широких закреплённых
// столбцов на узком экране забирали почти всю ширину, и данные не помещались
const DEFAULT_PINNING: DataGridColumnPinningState = { left: ['copyLink'], right: [] };

/**
 * DataGrid с серверными сортировкой, фильтрами и постраничностью: строки приходят окнами по мере
 * прокрутки, отбирает и упорядочивает их бэкенд. Колонки, сортировка и фильтры строятся через
 * выделенные хуки `useAllDetailsColumns`, `useAllDetailsSorting`, `useAllDetailsFilters`. Состав
 * и порядок колонок персистится через `onChangeColumnSettings`.
 */
export const AllDetailsTable: FC<Props> = ({
  totalCount,
  pageSize,
  cacheVersion,
  fetchPage,
  sampleRow,
  blockNames,
  availableColumns,
  availableAttributes,
  columnSettings,
  onChangeColumnSettings,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  sidePanel,
  onSidePanelChange,
  onOpenConversation,
}) => {
  const { t } = useTranslation('qualityDashboard');

  // Карта blockName → blockId по строке-образцу: blockNames приходят с бэка как набор имён
  // без id, но для серверной сортировки и фильтра `blockRating:<blockId>` нужен именно id.
  const blockIdByName = useMemo(() => {
    const map = new Map<string, number>();
    if (!sampleRow) return map;
    sampleRow.blocks.forEach((b) => {
      if (typeof b.blockId === 'number' && !map.has(b.blockName)) {
        map.set(b.blockName, b.blockId);
      }
    });
    return map;
  }, [sampleRow]);

  const columns = useAllDetailsColumns({
    blockNames,
    availableColumns,
    availableAttributes,
    blockIdByName,
    t,
  });

  // Дефолтный порядок: обязательные первыми (в их фиксированном порядке), затем остальные
  const defaultOrder = useMemo(() => columns.map((c) => c.id), [columns]);
  const optionalIds = useMemo(
    () => defaultOrder.filter((id) => !MANDATORY_COLUMN_IDS.includes(id as never)),
    [defaultOrder],
  );

  // Видимость по умолчанию: при первом открытии скрываем атрибутные и экспертные колонки, а с
  // ними и колонки полей записи разговора; основная и критериальная вертикаль таблицы остаётся
  // видимой как раньше.
  const defaultVisibility = useMemo<DataGridColumnVisibilityState>(() => {
    const vis: DataGridColumnVisibilityState = {};
    columns.forEach((c) => {
      if (c.id.startsWith('attr_') || c.id.startsWith('er_') || RECORD_FIELD_COLUMN_IDS.includes(c.id as never)) {
        vis[c.id] = false;
      } else {
        vis[c.id] = true;
      }
    });
    return vis;
  }, [columns]);

  // Стартовый порядок: либо сохранённый, либо дефолтный
  const initialOrder = useMemo<DataGridColumnOrderState>(() => {
    const saved = columnSettings?.columnOrder;
    if (!saved || saved.length === 0) return defaultOrder;
    // Сохраняем обязательные в фиксированном порядке впереди, затем сохранённые,
    // затем новые (которых ещё не было в сохранённом порядке).
    const mandatory = [...MANDATORY_COLUMN_IDS];
    const savedOptional = saved.filter((id) => !mandatory.includes(id as never) && optionalIds.includes(id));
    const newOptional = optionalIds.filter((id) => !saved.includes(id));
    return [...mandatory, ...savedOptional, ...newOptional];
  }, [columnSettings?.columnOrder, defaultOrder, optionalIds]);

  // Стартовая видимость: обязательные = true; остальные — из сохранённого списка, либо дефолтные
  const initialVisibility = useMemo<DataGridColumnVisibilityState>(() => {
    const saved = columnSettings?.visibleColumns;
    if (!saved) return defaultVisibility;
    // Колонки, о которых сохранённые настройки вообще ничего не знают (их не было ни
    // среди видимых, ни в порядке), пользователь не скрывал — им положена дефолтная
    // видимость. Иначе набор колонок, изменившийся после сохранения настроек, остался
    // бы скрытым навсегда.
    const known = new Set([...saved, ...(columnSettings?.columnOrder ?? [])]);
    const vis: DataGridColumnVisibilityState = {};
    columns.forEach((c) => {
      if (MANDATORY_COLUMN_IDS.includes(c.id as never)) {
        vis[c.id] = true;
        return;
      }
      // Если колонка явно в saved.visibleColumns — видима; если знакома, но не в нём — скрыта
      vis[c.id] = known.has(c.id) ? saved.includes(c.id) : defaultVisibility[c.id];
    });
    return vis;
  }, [columnSettings?.visibleColumns, columnSettings?.columnOrder, columns, defaultVisibility]);

  const { gridProps } = useDataGridColumnsState({
    defaultOrder: initialOrder,
    defaultVisibility: initialVisibility,
    defaultPinning: DEFAULT_PINNING,
  });

  // «Очистить» возвращает вид пользователя, ни разу не настраивавшего таблицу. Сброс самого хука
  // кита для этого не годится: он возвращает к значениям, зафиксированным при монтировании, то
  // есть к сохранённым настройкам.
  const handleResetColumns = () => {
    gridProps.onColumnOrderChange(defaultOrder);
    gridProps.onColumnVisibilityChange(defaultVisibility);
    gridProps.onColumnPinningChange(DEFAULT_PINNING);
  };

  // Гарантируем, что обязательные колонки всегда видимы — даже если в saved.visibleColumns
  // их нет (защита от рассинхронизации). Срабатывает после изменений видимости пользователем.
  const lastSavedRef = useRef<{ visible: string[]; order: string[] } | null>(null);

  useEffect(() => {
    const order = gridProps.columnOrder ?? initialOrder;
    const visibility = gridProps.columnVisibility ?? initialVisibility;

    // Список видимых необязательных колонок (для visibleColumns)
    const visibleOptional = order.filter(
      (id) => !MANDATORY_COLUMN_IDS.includes(id as never) && visibility[id] !== false,
    );

    const next = { visibleColumns: visibleOptional, columnOrder: order };
    const prev = lastSavedRef.current;
    if (
      prev &&
      prev.visible.length === next.visibleColumns.length &&
      prev.visible.every((v, i) => v === next.visibleColumns[i]) &&
      prev.order.length === next.columnOrder.length &&
      prev.order.every((v, i) => v === next.columnOrder[i])
    ) {
      return;
    }
    lastSavedRef.current = { visible: next.visibleColumns, order: next.columnOrder };
    onChangeColumnSettings(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridProps.columnOrder, gridProps.columnVisibility]);

  const { sortingForGrid, handleSortingChange } = useAllDetailsSorting({ sort, onSortChange });

  const { filtersForGrid, handleFiltersChange } = useAllDetailsFilters({ filters, onFiltersChange });

  // Обязательные колонки не скрываются: панель их предлагает, но снятая галочка не
  // применяется — иначе кнопку копирования или консультанта можно потерять навсегда.
  const handleColumnVisibilityChange = (next: DataGridColumnVisibilityState) => {
    const forced: DataGridColumnVisibilityState = { ...next };
    MANDATORY_COLUMN_IDS.forEach((id) => {
      forced[id] = true;
    });
    gridProps.onColumnVisibilityChange?.(forced);
  };

  return (
    <div onMouseDown={preventShiftSelection}>
      <DataGrid
        {...gridProps}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        columns={columns}
        dataSource={{
          mode: 'lazy',
          totalCount,
          pageSize,
          fetchPage,
          cacheVersion,
        }}
        getRowId={(r) => String(r.id)}
        enableColumnResizing
        enableColumnReordering
        enableColumnPinning
        sortMode="single"
        sorting={sortingForGrid}
        onSortingChange={handleSortingChange}
        filters={filtersForGrid}
        onFiltersChange={handleFiltersChange}
        columnsPanel={{
          showPinning: true,
          onReset: handleResetColumns,
          width: sidePanel.columnsPanelWidth,
          onWidthChange: (columnsPanelWidth) => onSidePanelChange({ columnsPanelWidth }),
        }}
        activeSidePanel={sidePanel.activePanel}
        onActiveSidePanelChange={(activePanel) => onSidePanelChange({ activePanel })}
        sidePanelPinned={sidePanel.pinned}
        onSidePanelPinnedChange={(pinned) => onSidePanelChange({ pinned })}
        onRowClick={(row, ctx) => onOpenConversation(row.conversationId, ctx.event.shiftKey)}
        // Тот же потолок, что и у остальных блоков дашборда, но не выше видимой части экрана:
        // на невысоком экране жёсткая высота выталкивала остальную страницу за его пределы
        height={`min(${DASHBOARD_BLOCK_MAX_HEIGHT}px, 70dvh)`}
      />
    </div>
  );
};
